import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { useCart } from "../lib/cart";
import Reveal from "../components/Reveal";

const INDIAN_STATES = ["Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Jammu & Kashmir"];

const inputCls = "w-full bg-[#151821] border border-white/15 px-4 py-3.5 font-mono text-xs tracking-wider text-ink placeholder:text-mute focus:outline-none focus:border-acid transition-colors";

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block font-mono text-[10px] tracking-[0.25em] text-dim mb-2">{label}</label>
      <input data-testid={testid} className={inputCls} {...props} />
    </div>
  );
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address1: "", address2: "", city: "", state: "Delhi", pincode: "" });
  const [payment, setPayment] = useState("prepaid");
  const [codInfo, setCodInfo] = useState(null);
  const [prepaidEnabled, setPrepaidEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  const codFee = payment === "cod" ? 49 : 0;
  const total = subtotal + codFee;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/payments/config").then((r) => {
      setPrepaidEnabled(r.data.prepaid_enabled);
      if (!r.data.prepaid_enabled) setPayment("cod");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("canceled")) {
      toast.error("Payment cancelled — your order is saved as pending payment");
      navigate("/checkout", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!/^\d{6}$/.test(form.pincode)) { setCodInfo(null); return; }
    const t = setTimeout(() => {
      api.get(`/pincode/${form.pincode}`).then((r) => setCodInfo(r.data)).catch(() => setCodInfo(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.pincode]);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your cart is empty");
    if (payment === "cod" && codInfo && !codInfo.cod_available) return toast.error("COD not available at this pincode");
    setBusy(true);
    try {
      const payload = {
        items: items.map((it) => ({ product_id: it.productId, color: it.color, size: it.size, quantity: it.qty })),
        customer: { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone },
        address: { address1: form.address1, address2: form.address2, city: form.city, state: form.state, pincode: form.pincode },
        payment_method: payment,
      };
      const { data } = await api.post("/checkout", payload);

      if (data.mode === "cod") {
        clear();
        navigate(`/order/${data.order_number}`);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Payment SDK failed to load. Try again."); setBusy(false); return; }
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: Math.round(data.amount * 100),
        currency: "INR",
        name: "RVIOR",
        description: `Order ${data.order_number}`,
        order_id: data.razorpay_order_id,
        prefill: { name: `${form.first_name} ${form.last_name}`, email: form.email, contact: form.phone },
        theme: { color: "#FF5500" },
        handler: async (res) => {
          try {
            const r = await api.post("/checkout/verify", {
              order_number: data.order_number,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            clear();
            navigate(`/order/${r.data.order.order_number}`);
          } catch (err) {
            toast.error(apiErrorDetail(err));
            setBusy(false);
          }
        },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Order saved as pending."); setBusy(false); });
      rzp.open();
      setBusy(false);
    } catch (err) {
      toast.error(apiErrorDetail(err));
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="pt-40 pb-24 text-center">
        <p className="font-mono text-[11px] tracking-widest text-mute">YOUR CART IS EMPTY.</p>
        <button onClick={() => navigate("/shop")} className="mt-6 bg-volt text-void font-display font-bold text-sm tracking-widest px-8 py-4 hover:bg-acid transition-colors">SHOP DROP 001</button>
      </main>
    );
  }

  return (
    <main className="px-5 md:px-10 pt-28 pb-24 max-w-[1400px] mx-auto">
      <Reveal className="mb-12">
        <p className="font-mono text-[11px] tracking-[0.3em] text-volt mb-3">SECURE CHECKOUT</p>
        <h1 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl">LOCK YOUR UNIT</h1>
      </Reveal>

      <form onSubmit={submit} className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-10">
          <section>
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim border-b border-white/10 pb-3 mb-6">01 — CONTACT</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="FIRST NAME *" testid="checkout-first-name-input" required value={form.first_name} onChange={set("first_name")} placeholder="Aarav" />
              <Field label="LAST NAME" testid="checkout-last-name-input" value={form.last_name} onChange={set("last_name")} placeholder="Malhotra" />
              <Field label="EMAIL *" testid="checkout-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@underground.in" />
              <Field label="PHONE *" testid="checkout-phone-input" required value={form.phone} onChange={set("phone")} placeholder="98XXXXXXXX" minLength={10} maxLength={15} pattern="[0-9]{10,15}" />
            </div>
          </section>

          <section>
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim border-b border-white/10 pb-3 mb-6">02 — SHIPPING ADDRESS</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="ADDRESS LINE 1 *" testid="checkout-address1-input" required value={form.address1} onChange={set("address1")} placeholder="Flat, Street, Landmark" />
              </div>
              <div className="sm:col-span-2">
                <Field label="ADDRESS LINE 2" testid="checkout-address2-input" value={form.address2} onChange={set("address2")} placeholder="Area (optional)" />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.25em] text-dim mb-2">CITY *</label>
                <input data-testid="checkout-city-input" required className={inputCls} value={form.city} onChange={set("city")} placeholder="New Delhi" />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-[0.25em] text-dim mb-2">STATE *</label>
                <select data-testid="checkout-state-input" required className={inputCls} value={form.state} onChange={set("state")}>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Field label="PINCODE *" testid="checkout-pincode-input" required value={form.pincode} onChange={set("pincode")} placeholder="110001" pattern="[0-9]{6}" maxLength={6} />
              </div>
              <div className="flex items-end">
                {codInfo && (
                  <p data-testid="pincode-check-result" className={`font-mono text-[10px] tracking-widest px-3 py-2 border ${codInfo.cod_available ? "text-[#22C55E] border-[#22C55E]/40" : "text-volt border-volt/40"}`}>
                    {codInfo.cod_available ? "COD AVAILABLE · 3-7 BUSINESS DAYS" : "COD NOT AVAILABLE HERE · PREPAID ONLY"}
                  </p>
                )}
              </div>
            </div>
          </section>

          <section>
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim border-b border-white/10 pb-3 mb-6">03 — PAYMENT</p>
            <div className="space-y-3">
              <label className={`flex items-center justify-between border p-5 transition-colors ${payment === "prepaid" ? "border-acid bg-[#151821]" : "border-white/15 hover:border-white/30"} ${prepaidEnabled ? "cursor-pointer" : "opacity-40 pointer-events-none"}`}>
                <div className="flex items-center gap-4">
                  <input data-testid="checkout-payment-prepaid-radio" type="radio" name="payment" checked={payment === "prepaid"} onChange={() => setPayment("prepaid")} disabled={!prepaidEnabled} className="accent-[#DFFF00]" />
                  <div>
                    <p className="font-display font-bold text-sm tracking-widest">
                      PREPAID {prepaidEnabled ? "— UPI / GPAY / PHONEPE" : "— TEMPORARILY OFFLINE"}
                    </p>
                    <p className="font-mono text-[10px] text-mute tracking-widest mt-1">CARDS · NETBANKING · VIA RAZORPAY · FREE SHIPPING</p>
                  </div>
                </div>
                <span className="font-mono text-[10px] tracking-widest text-[#3B82F6]">SECURE</span>
              </label>
              <label className={`flex items-center justify-between border p-5 transition-colors ${payment === "cod" ? "border-acid bg-[#151821]" : "border-white/15 hover:border-white/30"} ${codInfo && !codInfo.cod_available ? "opacity-40 pointer-events-none" : "cursor-pointer"}`}>
                <div className="flex items-center gap-4">
                  <input data-testid="checkout-payment-cod-radio" type="radio" name="payment" checked={payment === "cod"} onChange={() => setPayment("cod")} className="accent-[#DFFF00]" />
                  <div>
                    <p className="font-display font-bold text-sm tracking-widest">CASH ON DELIVERY {codInfo && !codInfo.cod_available && "— NOT AT THIS PINCODE"}</p>
                    <p className="font-mono text-[10px] text-mute tracking-widest mt-1">PAY AT YOUR DOOR · +₹49 COD FEE</p>
                  </div>
                </div>
                <span className="font-mono text-[10px] tracking-widest text-[#22C55E]">COD</span>
              </label>
            </div>
          </section>
        </div>

        {/* SUMMARY */}
        <aside className="lg:col-span-2">
          <div className="border border-white/10 bg-[#0E1015] p-6 lg:sticky lg:top-24">
            <p className="font-mono text-[11px] tracking-[0.3em] text-dim border-b border-white/10 pb-3 mb-5">ORDER SUMMARY</p>
            <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
              {items.map((it, i) => (
                <div key={`${it.productId}-${it.color}-${it.size}`} className="flex gap-3">
                  <img src={it.image} alt={it.title} className="w-12 h-14 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-[11px] font-bold uppercase truncate">{it.title}</p>
                    <p className="font-mono text-[10px] text-mute mt-0.5">{it.color} / {it.size} × {it.qty}</p>
                  </div>
                  <p className="font-mono text-xs">₹{(it.price * it.qty).toLocaleString("en-IN")}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 mt-5 pt-5 space-y-2 font-mono text-xs">
              <div className="flex justify-between text-dim"><span>SUBTOTAL</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-dim"><span>SHIPPING</span><span className="text-acid">FREE</span></div>
              {codFee > 0 && <div className="flex justify-between text-dim"><span>COD FEE</span><span>₹49.00</span></div>}
              <div data-testid="checkout-total" className="flex justify-between text-base font-bold pt-2 border-t border-white/10"><span>TOTAL</span><span className="text-acid">₹{total.toLocaleString("en-IN")}</span></div>
            </div>
            <button
              data-testid="checkout-submit-order-btn"
              type="submit"
              disabled={busy}
              className="w-full mt-6 bg-volt text-void font-display font-bold text-sm tracking-widest py-4 disabled:opacity-40 hover:bg-acid transition-colors duration-300"
            >
              {busy ? "PROCESSING…" : payment === "cod" ? "PLACE COD ORDER" : "PAY SECURELY NOW"}
            </button>
            <p className="mt-4 font-mono text-[9px] tracking-widest text-mute text-center leading-relaxed">
              ON PAYMENT, YOUR ORDER GOES STRAIGHT INTO PRODUCTION
            </p>
          </div>
        </aside>
      </form>
    </main>
  );
}
